import { clsx } from 'clsx';
import { useState } from 'react';
import Animated, { FadeIn } from 'react-native-reanimated';
import { View } from 'react-native';

import { Button } from '../button';
import { Box } from '../ui/box';
import { Text } from '../ui/text';

type NewsletterProps = {
  showTitle?: boolean;
  withBottomMargin?: boolean;
};

export default function Newsletter({
  showTitle,
  withBottomMargin = true,
}: NewsletterProps) {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<
    'idle' | 'loading' | 'success' | 'error'
  >('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus('loading');

    if (!email) {
      setStatus('error');
      return;
    }

    try {
      const response = await fetch('https://subs.reactiive.io/api/subscribe', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setStatus('success');
        setEmail('');
      } else {
        setStatus('error');
        console.error('Subscription failed:', data.error);
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setStatus('error');
    }
  };

  if (status === 'success') {
    return (
      <Box
        className={clsx('w-full mt-6', {
          'mb-32': withBottomMargin,
        })}
      >
        <Animated.View entering={FadeIn.duration(300)}>
          <Text variant="body" className="text-success">
            You're subscribed! Check your inbox for confirmation.
          </Text>
        </Animated.View>
      </Box>
    );
  }

  return (
    <Box
      className={clsx('w-full mt-6', {
        'mb-32': withBottomMargin,
      })}
    >
      {showTitle && (
        <View className="mb-2">
          <Text variant="h5">Newsletter</Text>
        </View>
      )}
      <View className="mb-4">
        <Text variant="body" className="text-text-secondary">
          Join 5,000+ devs getting occasional updates on React Native
          animations.
        </Text>
      </View>

      <form onSubmit={handleSubmit} className="flex flex-row gap-2">
        <input
          type="email"
          placeholder="you@email.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={status === 'loading'}
          className={clsx(
            'flex-1 px-4 py-2 rounded-md',
            'bg-surface-elevated border border-border',
            'text-text-primary text-sm',
            'outline-none transition-colors duration-200',
            'focus:border-primary/60',
            'disabled:opacity-50',
            '[&::placeholder]:text-text-tertiary'
          )}
        />
        <Button
          type="submit"
          disabled={status === 'loading'}
          variant="primary"
          size="md"
          rounded="md"
          loading={status === 'loading'}
        >
          Subscribe
        </Button>
      </form>

      {status === 'error' && (
        <Animated.View entering={FadeIn.duration(200)} className="mt-3">
          <Text variant="body" className="text-danger text-sm">
            Something went wrong. Please try again.
          </Text>
        </Animated.View>
      )}
    </Box>
  );
}
